import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Users, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const AssignmentDistributionChart = () => {
  const [chartState, setChartState] = useState({
    loading: true,
    error: null,
    data: [],
    total: 0
  });

  // Color palette for pie chart segments
  const COLORS = [
    '#3B82F6', // Blue
    '#10B981', // Green
    '#F59E0B', // Yellow
    '#EF4444', // Red
    '#8B5CF6', // Purple
    '#06B6D4', // Cyan
    '#F97316', // Orange
    '#84CC16', // Lime
    '#EC4899', // Pink
    '#6B7280'  // Gray (for unassigned)
  ];

  useEffect(() => {
    loadAssignmentData();
  }, []);

  const loadAssignmentData = async () => {
    setChartState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await axios.get('/api/submissions/analytics/assignment-distribution');
      
      setChartState({
        loading: false,
        error: null,
        data: response.data.data || [],
        total: response.data.total || 0
      });
      
    } catch (error) {
      console.error('Load assignment analytics error:', error);
      setChartState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load assignment analytics'
      }));
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900">{data.userName}</p>
          <p className="text-sm text-blue-600">
            {`Submissions: ${data.count} (${data.percentage}%)`}
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="flex flex-wrap gap-2 mt-4">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-700 truncate max-w-24">
              {entry.payload.userName}
            </span>
            <span className="text-gray-500">
              ({entry.payload.count})
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (chartState.loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading assignment data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Users className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Assignment Distribution</h3>
        </div>
        <button
          onClick={loadAssignmentData}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {chartState.error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{chartState.error}</p>
              <button
                onClick={loadAssignmentData}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {chartState.data.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No assignment data</h3>
          <p className="mt-1 text-sm text-gray-500">
            No submissions have been assigned yet.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{chartState.total}</p>
                <p className="text-sm text-gray-500">Total Submissions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {chartState.data.filter(item => item.userId !== null).length}
                </p>
                <p className="text-sm text-gray-500">Assigned Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-600">
                  {chartState.data.find(item => item.userId === null)?.count || 0}
                </p>
                <p className="text-sm text-gray-500">Unassigned</p>
              </div>
            </div>
          </div>

          {/* Pie Chart */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartState.data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  paddingAngle={2}
                  dataKey="count"
                >
                  {chartState.data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.userId === null ? '#6B7280' : COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  content={<CustomLegend />}
                  wrapperStyle={{ paddingTop: '20px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default AssignmentDistributionChart;
